import React, { useRef, useState } from "react";
import { useClickOutside } from "@/utils";

type Ancestor = {
  id: string | number;
  name: string;
  code: string;
};

type TaxonomyItem = {
  id: string | number;
  name: string;
  code: string;
  ancestors?: Ancestor[];
};

type SelectedTaxonomy = {
  id: string | number;
  name: string;
  code: string;
};

type Props = {
  taxonomyList: TaxonomyItem[];
  selectedTaxonomy: SelectedTaxonomy[];
  onSearch: (keyword: string) => void;
  onAdd: (item: SelectedTaxonomy) => void;
  isSearchLoading?: boolean;
};

const TaxonomySearch: React.FC<Props> = ({
  taxonomyList,
  selectedTaxonomy,
  onSearch,
  onAdd,
  isSearchLoading = false,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<string | number | null>(null);
  const [hoveredAncestor, setHoveredAncestor] = useState<{
    itemId: string | number;
    ancestorId: string | number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setOpen(false));

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    setIsLoadingComplete(false);
    if (val.trim()) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
    onSearch(val);
  };

  const isAlreadyAdded = (id: string | number) =>
    selectedTaxonomy.some((t) => String(t.id) === String(id));

  const handleAdd = (item: SelectedTaxonomy) => {
    onAdd(item);
    setOpen(false);
  };

  const showResultsDropdown = Boolean(
    open && query.trim() && !isSearchLoading
  );

  return (
    <div className="position-relative" ref={containerRef}>
      {/* Search input — progress bar matches Select (scaleX, 2s) */}
      <div className="position-relative">
        <i
          className="bi bi-search position-absolute text-secondary"
          style={{
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            fontSize: 13,
          }}
        />
        <input
          type="text"
          className="form-control"
          style={{
            paddingLeft: 36,
            fontSize: 13,
            borderRadius: showResultsDropdown ? "4px 4px 0 0" : undefined,
          }}
          placeholder="Search taxonomy..."
          value={query}
          onChange={handleInput}
          onFocus={() => setOpen(true)}
        />
        {isLoading && query.trim() && !isLoadingComplete && (
          <div
            className="position-absolute bottom-0 start-0 w-100"
            style={{ height: "2px" }}
          >
            <div
              key={query}
              className="bg-primary"
              style={{
                width: "100%",
                height: "100%",
                animation: "progress 2s ease-in-out forwards",
                background: "#0d6efd",
                transformOrigin: "left",
                transform: "scaleX(0)",
              }}
              onAnimationEnd={() => {
                setIsLoadingComplete(true);
                setIsLoading(false);
              }}
            />
            <style>
              {`
                @keyframes progress {
                  0% {
                    transform: scaleX(0);
                  }
                  100% {
                    transform: scaleX(1);
                  }
                }
              `}
            </style>
          </div>
        )}
        {!isLoading && isSearchLoading && query.trim() && (
          <div
            className="position-absolute bottom-0 start-0 w-100 bg-primary"
            style={{ height: "2px" }}
            aria-hidden
          />
        )}
      </div>

      {/* Dropdown: results or empty (RPC done); no separate "searching" row */}
      {showResultsDropdown && (
        <div
          className="bg-white border shadow"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            maxHeight: 300,
            overflowY: "auto",
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
          }}
        >
          {taxonomyList.length === 0 && (
            <div
              className="d-flex align-items-center justify-content-center py-4 px-3 text-muted"
              style={{ fontSize: 13, minHeight: 72 }}
            >
              No matches found
            </div>
          )}
          {taxonomyList.length > 0 &&
            taxonomyList.map((item) => {
              const ancestors: Ancestor[] = Array.isArray(item.ancestors)
                ? item.ancestors
                : [];
              const isRowHovered = hoveredRow === item.id;
              const allEntries = [
                ...ancestors,
                { id: item.id, name: item.name, code: item.code },
              ];

              return (
                <div
                  key={item.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "220px 1fr",
                    borderBottom: "1px solid #f0f0f0",
                    background: isRowHovered ? "#f8f9fa" : "#fff",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAncestor(null);
                  }}
                >
                  {/* Left: + adds this row’s result (leaf), same as picking the last segment on the right */}
                  <div
                    className="d-flex align-items-center gap-2 px-2 py-2"
                    style={{
                      borderRight: "1px solid #e9ecef",
                      minWidth: 0,
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0 flex-shrink-0 d-inline-flex align-items-center justify-content-center"
                      style={{
                        lineHeight: 1,
                        minWidth: 22,
                        color: "#1a56db",
                        cursor: "pointer",
                      }}
                      title={
                        isAlreadyAdded(item.id)
                          ? `Select "${item.name}"`
                          : `Add "${item.name}"`
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAdd({
                          id: item.id,
                          name: item.name,
                          code: item.code,
                        });
                      }}
                    >
                      <i
                        className="bi bi-plus-circle-fill"
                        style={{ fontSize: 15 }}
                      />
                    </button>
                    <div
                      className="d-flex flex-column justify-content-center"
                      style={{ flex: "1 1 0", minWidth: 0 }}
                    >
                      <div
                        className="fw-semibold text-truncate"
                        style={{ fontSize: 13, color: "#212529" }}
                        title={item.name}
                      >
                        {item.name}
                      </div>
                      <div
                        className="text-muted text-truncate"
                        style={{ fontSize: 11, marginTop: 2 }}
                      >
                        {item.code}
                      </div>
                    </div>
                  </div>

                  {/* Right: item name > ancestor1 > ancestor2 ... */}
                  <div
                    className="d-flex align-items-center px-3 py-2"
                    style={{ gap: 4, flexWrap: "wrap", minWidth: 0 }}
                  >
                    {allEntries.map((entry, idx) => {
                      const isAncHovered =
                        hoveredAncestor?.itemId === item.id &&
                        hoveredAncestor?.ancestorId === entry.id;
                      const alreadyAdded = isAlreadyAdded(entry.id);

                      return (
                        <React.Fragment key={entry.id}>
                          {idx > 0 && (
                            <span
                              className="text-muted"
                              style={{ fontSize: 11, flexShrink: 0 }}
                            >
                              {">"}
                            </span>
                          )}
                          <span
                            className="d-inline-flex align-items-center gap-1"
                            style={{
                              fontSize: 12,
                              padding: "2px 6px",
                              borderRadius: 4,
                              background: isAncHovered
                                ? "#e8f0fe"
                                : "transparent",
                              color: isAncHovered ? "#1a56db" : "#495057",
                              cursor: "pointer",
                              transition: "background 0.15s, color 0.15s",
                              whiteSpace: "nowrap",
                              border: isAncHovered
                                ? "1px solid #b3c8fd"
                                : "1px solid transparent",
                            }}
                            onMouseEnter={() =>
                              setHoveredAncestor({
                                itemId: item.id,
                                ancestorId: entry.id,
                              })
                            }
                            onMouseLeave={() => setHoveredAncestor(null)}
                            onClick={() =>
                              handleAdd({
                                id: entry.id,
                                name: entry.name,
                                code: entry.code,
                              })
                            }
                            title={
                              alreadyAdded
                                ? `Select "${entry.name}"`
                                : `Add "${entry.name}"`
                            }
                          >
                            {isAncHovered ? (
                              <i
                                className="bi bi-plus-circle-fill"
                                style={{ fontSize: 11 }}
                              />
                            ) : null}
                            {entry.name}
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default TaxonomySearch;
